import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';

const STORAGE_KEY = 'sv_consent';

/**
 * GDPR / ePrivacy cookie consent banner.
 *
 * Shown on first visit; persists the user's choice in localStorage so it
 * doesn't reappear on subsequent sessions. Analytics (PostHog + first-party
 * `/api/events`) are gated on an "accepted" value — nothing fires until the
 * user grants consent.
 *
 * Choices:
 *   • Accept → `sv_consent = 'accepted'` + fires `window.__sv_initPosthog()` + `window.__sv_initGtm()`
 *   • Reject → `sv_consent = 'rejected'` (banner stays dismissed, no tracking)
 *   • Read more → links to the existing `/cookie-policy` page
 */
export function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v !== 'accepted' && v !== 'rejected') {
        // Delay so banner doesn't flash during the initial page paint.
        const id = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(id);
      }
    } catch {
      // localStorage disabled → assume no consent given, show banner.
      setVisible(true);
    }
    return undefined;
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, 'accepted'); } catch (_) { /* ignore */ }
    if (typeof window !== 'undefined' && typeof window.__sv_initPosthog === 'function') {
      window.__sv_initPosthog();
    }
    if (typeof window !== 'undefined' && typeof window.__sv_initGtm === 'function') {
      window.__sv_initGtm();
    }
    if (typeof window !== 'undefined' && typeof window.__sv_initGa4 === 'function') {
      window.__sv_initGa4();
    }
    setVisible(false);
  };

  const reject = () => {
    try { localStorage.setItem(STORAGE_KEY, 'rejected'); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-label={t('consent_aria')}
          aria-live="polite"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-consent-banner"
          className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-1/2 sm:right-auto sm:w-[460px] sm:max-w-[calc(100vw-3rem)] z-[10000]
                     rounded-2xl bg-white border border-[#e5ecf4]
                     shadow-[0_10px_40px_rgba(15,59,99,0.18)]
                     p-5 sm:p-6 sm:-ml-[230px]"
          style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}
        >
          <h2
            className="text-[18px] sm:text-[20px] font-bold leading-tight mb-2"
            style={{ color: '#0F3B63' }}
          >
            {t('consent_title')}
          </h2>
          <p
            className="text-[14px] sm:text-[15px] leading-snug mb-4"
            style={{ color: '#2d5f8d' }}
          >
            {t('consent_body')}{' '}
            <Link
              to="/cookie-policy"
              className="underline font-semibold"
              style={{ color: '#0184ff' }}
              data-testid="cookie-consent-policy-link"
            >
              {t('legal_cookie_policy')}
            </Link>
            .
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={reject}
              data-testid="cookie-consent-reject-btn"
              className="flex-1 h-10 px-4 rounded-full font-semibold text-[14px]
                         bg-white border border-[#d5dde8] text-[#0F3B63]
                         hover:bg-[#f7f8fa] active:scale-[0.98] transition-all"
            >
              {t('consent_reject')}
            </button>
            <button
              type="button"
              onClick={accept}
              data-testid="cookie-consent-accept-btn"
              className="flex-1 h-10 px-4 rounded-full font-semibold text-[14px]
                         bg-[#0184ff] hover:bg-[#0170dc] text-white
                         active:scale-[0.98] transition-all"
            >
              {t('consent_accept')}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CookieConsentBanner;
